import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { UpdateStatusDialog } from './UpdateStatusDialog';
import { getAdminStatusDialogConfig } from './adminStatusDialogConfig';
import { useAdminList } from './useAdminList';
import type { FormattedAdmin } from './adminListValidation';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';

export default function AdminList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showAddUserSuccessSnackbar, setShowAddUserSuccessSnackbar] =
    useState(false);
  const [showEditAdminSuccessSnackbar, setShowEditAdminSuccessSnackbar] =
    useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SPACE_BELOW_FOR_MENU = 70;
  // const [showVerifySuccessSnackbar, setShowVerifySuccessSnackbar] =
  // useState(false);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FormattedAdmin | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar states
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');

  const {
    admins,
    loading,
    page,
    setPage,
    limit,
    // setLimit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    dateRange,
    setDateRange,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    updateUserStatus,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
  } = useAdminList();

  // Show success snackbar if navigated from add user
  useEffect(() => {
    if (location.state?.showAddUserSuccess) {
      setShowAddUserSuccessSnackbar(true);
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Show success snackbar if navigated from edit admin
  useEffect(() => {
    if (location.state?.showEditAdminSuccess) {
      setShowEditAdminSuccessSnackbar(true);
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Close actions menu when clicking outside
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  const columns: Column<FormattedAdmin>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Imię i nazwisko',
        width: '25.91%', // 256px at 988px table width
        render: (row) => {
          return (
            <div className="flex items-center w-full min-w-0 gap-3">
              <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0">
                <span className="text-[#223746] text-base font-medium">
                  {row.initials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Tooltip content={row.name}>
                  <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px]  overflow-hidden text-ellipsis whitespace-nowrap block">
                    {row.name}
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        key: 'user_id',
        header: 'Admin ID',
        width: '20.65%', // 204px at 988px table width
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {row.user_id}
          </span>
        ),
      },
      {
        key: 'created_at',
        header: 'Data utworzenia',
        width: '20.65%', // 204px at 988px table width
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {row.formattedCreatedAt}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '20.65%', // 204px at 988px table width
        render: (row, index) => (
          <div
            data-testid={`toggle-status-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(row);
              setIsDialogOpen(true);
            }}
            className="cursor-pointer inline-flex"
          >
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton
                checked={row.status === 'active'}
                onChange={() => {
                  // Prevent toggle from changing - only open dialog
                  // The toggle state should only change after successful API call
                  // This onChange is called but we don't update parent state,
                  // so the checked prop stays the same
                }}
                label={row.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                size="Small"
              />
            </div>
          </div>
        ),
      },
      {
        key: 'id',
        header: 'Akcje',
        width: '12.14%', // 120px at 988px table width
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative flex w-full items-center justify-center"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    const spaceBelow = visibleBottom - buttonRect.bottom;
                    setMenuOpensUpward(spaceBelow < MIN_SPACE_BELOW_FOR_MENU);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: 'Podgląd',
                      },
                      {
                        id: 'EDIT',
                        label: 'Edytuj',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(
                          `/administrators/Podgląd-Administratora/${row.id}`
                        );
                      } else if (itemId === 'EDIT') {
                        navigate(
                          `/administrators/Edycja-Administratora/${row.id}`
                        );
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate, setSelectedUser, setIsDialogOpen]
  );

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = () => {
    // Reset to last applied filters when closing without applying
    setDateRange(appliedFilters.dateRange);
    setStatusOptions(appliedFilters.statusOptions);
    setIsFilterOpen(false);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedUser(null);
    }
  };

  // Handle status update confirmation
  const handleConfirmStatusUpdate = async () => {
    if (!selectedUser) {
      return;
    }

    setIsSubmitting(true);
    const isActive = selectedUser.status === 'inactive'; // Will activate if currently inactive

    try {
      const result = await updateUserStatus(selectedUser.id, isActive);

      if (result.success) {
        // Show success snackbar
        const message = isActive
          ? 'Administrator został pomyślnie aktywowany.'
          : 'Administrator został pomyślnie dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedUser(null);
      } else {
        // Show error snackbar
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
      }
    } catch (_error) {
      // Show error snackbar
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // isSearchClearing is now calculated in the hook to handle both cases:
  // 1. Search is empty but debouncedSearch still has value (during debounce delay)
  // 2. debouncedSearch just became empty (after debounce completes, before loading starts)

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  // Check if there's truly no data (not loading, no results, no filters/search applied)
  // Only show "No Data" if current data is from a no-filter fetch AND totalCount is 0
  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    admins.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !appliedFilters.dateRange?.startDate &&
    !appliedFilters.dateRange?.endDate &&
    !appliedFilters.statusOptions.some((opt) => opt.checked);

  // Check if there are no results with filters/search applied
  // const hasNoResults =
  //   !loading &&
  //   admins.length === 0 &&
  //   (search.trim() ||
  //     appliedFilters.dateRange?.startDate ||
  //     appliedFilters.dateRange?.endDate ||
  //     appliedFilters.statusOptions.some((opt) => opt.checked));

  return (
    <>
      {/* Custom styles for status update success snackbar to match Figma */}
      <style>{`
        .status-update-success-snackbar {
          width: 432px !important;
          min-height: 48px !important;
          box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.15), 0px 4px 8px 3px rgba(0, 0, 0, 0.08) !important;
        }
        .status-update-success-snackbar > div {
          padding-left: 16px !important;
          padding-right: 48px !important;
          padding-top: 14px !important;
          padding-bottom: 14px !important;
          position: relative !important;
          min-height: 48px !important;
        }
        .status-update-success-snackbar p {
          color: #FFFFFF !important;
          font-family: 'Roboto', sans-serif !important;
          font-weight: 400 !important;
          font-size: 14px !important;
          line-height: 20px !important;
          letter-spacing: 0.25px !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }
        .status-update-success-snackbar > div > div:last-child {
          position: absolute !important;
          top: 50% !important;
          right: 16px !important;
          transform: translateY(-50%) !important;
        }
        .status-update-success-snackbar button[aria-label="Zamknij"] {
          color: #FFFFFF !important;
        }
        .status-update-success-snackbar button[aria-label="Zamknij"]:hover {
          background-color: rgba(24, 26, 27, 0.08) !important;
        }
      `}</style>
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        {/* Header: Title always visible; button hidden when no data */}
        <div
          className="flex items-center justify-between shrink-0"
          id="admin-list-content"
        >
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            Administratorzy
          </div>
          {!hasNoData && (
            <button
              onClick={() => navigate('/administrators/addUser')}
              className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                Dodaj administratora
              </span>
            </button>
          )}
        </div>

        {/* Show search/filter unless truly no data exists */}
        {!hasNoData && (
          <>
            {/* Search and Filter */}
            <div className="flex items-center justify-between shrink-0">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Szukaj po nazwie"
              />
              <div className={`dropdown ${isFilterOpen ? 'show' : ''}`}>
                <style>{`
                .dropdown {
                  position: relative;
                }

                .dropdown-content {
                  position: absolute;
                  background-color: #f9f9f9;
                  min-width: 130px;
                  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
                  padding: 12px 16px;
                }

                .dropdown.show .dropdown-content {
                  display: block;
                }
              `}</style>
                <FilterBar
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  isOpen={isFilterOpen}
                  filterCount={
                    (appliedFilters.dateRange?.startDate ? 1 : 0) +
                    (appliedFilters.dateRange?.endDate ? 1 : 0) +
                    appliedFilters.statusOptions.filter((opt) => opt.checked)
                      .length
                  }
                />
                <FilterPopup
                  isOpen={isFilterOpen}
                  onClose={handleCloseFilter}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  statusOptions={statusOptions}
                  onStatusChange={setStatusOptions}
                  onApply={handleApplyFiltersAndClose}
                  onClear={handleClearFilters}
                />
              </div>
            </div>
          </>
        )}

        {/* Table - Scrollable with sticky header */}
        <div
          ref={tableScrollContainerRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
        >
          {admins.length === 0 && !loading && !isSearchClearing ? (
            /* No data or no results */
            hasNoData ? (
              /* No Data State - no filters/search applied */
              <div className="flex flex-col items-center justify-center w-full h-full">
                <LottieAnimation
                  animationData={noDataAnimation}
                  width={277}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-start justify-start gap-1">
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                      Nie dodano jeszcze żadnych administratorów.
                    </div>
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                      Dodaj użytkowników, aby przyznać im dostęp do systemu i
                      przypisać zakres obowiązków.
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/administrators/addUser')}
                    className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center"
                  >
                    <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                      Dodaj administratora
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* No Results State - filters/search applied but no results */
              <div className="flex flex-col items-center justify-center w-full h-full gap-3">
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
                  <div className="w-full h-6 text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    Nie znaleziono wyników.
                  </div>
                  <div className="w-full h-5 text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                    {search.trim() &&
                    (appliedFilters.dateRange?.startDate ||
                      appliedFilters.dateRange?.endDate ||
                      appliedFilters.statusOptions.some((opt) => opt.checked))
                      ? 'Spróbuj zmienić kryteria wyszukiwania lub filtry.'
                      : search.trim()
                        ? 'Spróbuj zmienić kryteria wyszukiwania.'
                        : 'Spróbuj zmienić ustawienia filtrów.'}
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Table Content */
            <Table
              columns={columns}
              data={admins}
              loading={loading}
              loadingRows={limit}
              stickyHeader
              getRowKey={(row) => row.id}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'id' && openMenuRowId === row.id ? 50 : 1
              }
            />
          )}
        </div>

        {/* Pagination - Fixed at bottom */}
        {!hasNoData && totalCount > 10 && (
          <div
            className="shrink-0 px-4 -mx-4"
            ref={(_el) => {
              // Debug telemetry removed
            }}
          >
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </div>
            )}
          </div>
        )}

        {/* Add User Success Snackbar */}
        <Snackbar
          open={showAddUserSuccessSnackbar}
          message="Administrator został pomyślnie dodany. Wiadomość z tymczasowymi danymi do logowania została wysłana na jego skrzynkę odbiorczą."
          variant="success"
          onClose={() => setShowAddUserSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Edit Admin Success Snackbar */}
        <Snackbar
          open={showEditAdminSuccessSnackbar}
          message="Dane administratora zostały pomyślnie zaktualizowane."
          variant="success"
          onClose={() => setShowEditAdminSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Status Update Success Snackbar - Custom styling to match Figma specs */}
        <Snackbar
          open={showStatusSuccessSnackbar}
          message={statusSuccessMessage}
          variant="success"
          onClose={() => setShowStatusSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
          className="status-update-success-snackbar"
        />

        {/* Status Update Error Snackbar */}
        <Snackbar
          open={showStatusErrorSnackbar}
          message={statusErrorMessage}
          variant="error"
          onClose={() => setShowStatusErrorSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Activate/Deactivate User Dialog */}
        {selectedUser && (
          <UpdateStatusDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleConfirmStatusUpdate}
            isSubmitting={isSubmitting}
            {...getAdminStatusDialogConfig(
              selectedUser.name,
              selectedUser.status === 'active'
            )}
          />
        )}
      </div>
    </>
  );
}
